import React from "react";
import { TransformedPlan } from "../../../../Interface";
import cartService, { Cart } from "../../../../Service/cart.service";
import { getPlanAnnualModePricing } from "../../../../Utils/planUtils";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useNavigate } from "react-router-dom";
import { StepKey } from "../../types";

interface PlanCardProps {
  plan: TransformedPlan;
  userCart: Cart;
  handleStepChange: (step: StepKey) => void;
  isCurrentPlan: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  userCart,
  handleStepChange,
  isCurrentPlan,
}) => {
  const navigate = useNavigate();

  const renderMonthlyPricingInfo = () => {
    return (
      <div
        style={{
          borderBottom: "1px solid #eee",
          paddingBottom: "16px",
        }}
      >
        <span
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#000",
          }}
        >
          {plan.isDefaultEnterprisePlan
            ? "Custom"
            : `${plan.currencySymbol}${
                userCart.basePlan.billedAnnually
                  ? plan.price.annually
                  : plan.price.monthly
              }`}
        </span>
        <span style={{ color: "#888", marginLeft: "4px", fontSize: "16px" }}>
          {plan.isDefaultEnterprisePlan ? "" : "/month"}
        </span>
        {!plan.isDefaultEnterprisePlan && plan.currency === "INR" && (
          <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
            + taxes
          </div>
        )}
      </div>
    );
  };

  const renderAnnualPricingInfo = () => {
    const { totalAnnualSavings, annualModeMonthlyPrice, actualMonthlyPrice } =
      getPlanAnnualModePricing(plan);

    const price = plan.isDefaultEnterprisePlan
      ? "Custom"
      : plan.isOrgEnterprisePlan
        ? `${plan.currencySymbol}${actualMonthlyPrice}`
        : `${plan.currencySymbol}${annualModeMonthlyPrice}`;

    return (
      <div
        style={{
          borderBottom: "1px solid #eee",
          paddingBottom: "16px",
        }}
      >
        <span
          style={{
            fontSize: "32px",
            fontWeight: "700",
            color: "#000",
          }}
        >
          {price}{" "}
          {!plan.isDefaultEnterprisePlan && !plan.isOrgEnterprisePlan && (
            <s
              style={{ color: "#FF0024", fontWeight: "400", fontSize: "16px" }}
            >
              {plan.currencySymbol}
              {plan.price.monthly}
            </s>
          )}
        </span>
        <span style={{ color: "#888", marginLeft: "4px", fontSize: "16px" }}>
          {plan.isDefaultEnterprisePlan ? "" : "/month"}
        </span>

        {!plan.isDefaultEnterprisePlan && plan.currency === "INR" && (
          <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
            + taxes
          </div>
        )}

        {!plan.isDefaultEnterprisePlan && !plan.isOrgEnterprisePlan && (
          <div
            style={{
              marginTop: "8px",
              fontSize: "14px",
              color: "#52c41a",
            }}
          >
            Save{" "}
            <strong>
              {plan.currencySymbol}
              {totalAnnualSavings}
            </strong>{" "}
            /year
          </div>
        )}
      </div>
    );
  };

  const handlePlanSelection = (plan: TransformedPlan) => {
    if (plan.isDefaultEnterprisePlan) {
      navigate("/enterprise-plan");
    } else {
      cartService.setSelectedBasePlan(plan._id);
      handleStepChange("addons");
    }
  };

  return (
    <div className="plan-card">
      {/* Plan name and description */}
      <div>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "600",
            margin: "0 0 8px 0",
          }}
        >
          {plan.name}
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#666",
            margin: "0px",
          }}
        >
          {plan.description}
        </p>
      </div>

      {/* Price */}
      {userCart.basePlan.billedAnnually
        ? renderAnnualPricingInfo()
        : renderMonthlyPricingInfo()}

      {/* Features */}
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {plan.features.map((feature: any, j: any) => (
          <li
            key={j}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span style={{ fontSize: "14px", color: "#333" }}>{feature}</span>
          </li>
        ))}
        <li
          key={"custom-branding"}
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <span style={{ fontSize: "14px", color: "#333" }}>
            Custom branding
          </span>
          {plan.customBranding ? (
            <CheckOutlined
              style={{
                color: "#52c41a",
                marginLeft: "8px",
              }}
            />
          ) : (
            <CloseOutlined
              style={{
                color: "#FF0024",
                marginLeft: "8px",
              }}
            />
          )}
        </li>
        <li
          key={"custom-domain"}
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <span style={{ fontSize: "14px", color: "#333" }}>
            Custom domain linking
          </span>
          {plan.customDomainLinking ? (
            <CheckOutlined
              style={{
                color: "#52c41a",
                marginLeft: "8px",
              }}
            />
          ) : (
            <CloseOutlined
              style={{
                color: "#FF0024",
                marginLeft: "8px",
              }}
            />
          )}
        </li>
      </ul>

      {/* Button */}
      {!isCurrentPlan && (
        <div>
          <Button
            size="middle"
            type={userCart.basePlan.planId === plan._id ? "default" : "primary"}
            block
            onClick={() => handlePlanSelection(plan)}
            disabled={isCurrentPlan}
          >
            {plan.isDefaultEnterprisePlan
              ? "Contact Sales"
              : userCart.basePlan.planId === plan._id
                ? "Selected Plan"
                : "Select"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PlanCard;
